import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Label from 'types/Label';
import { Icon, Modal } from 'components/ui';
import useLabelTranslation from 'hooks/useLabelTranslation';

import Prompt from '../Prompt';
import MandatorySymbol from '../MandatorySymbol';

export interface ContentProps {
  title: Label | string;
  description: Label | string;
  placeholder?: string;
  editable?: boolean;
  mandatory?: boolean;
  onChangeName?: (value: string) => void;
}

const Content: React.FC<ContentProps> = ({
  title, description, editable, mandatory, placeholder, onChangeName,
}) => {
  const intl = useIntl();
  const { getLabelTranslation } = useLabelTranslation();

  const displayedTitle = typeof title === 'string' ? title : getLabelTranslation(title);
  const displayedDescription = typeof description === 'string' ? description : getLabelTranslation(description);

  const descriptionRef = React.useRef<HTMLSpanElement>(null);
  const [isInstructionModalOpen, setInstructionIsModalOpen] = React.useState(false);
  const [isReadMoreVisible, setIsReadMoreVisible] = React.useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);

  React.useLayoutEffect(() => {
    const setReadMore = () => {
      setIsReadMoreVisible(descriptionRef.current?.scrollHeight > descriptionRef.current?.clientHeight);
    };

    setReadMore();
    window.addEventListener('resize', setReadMore);
    return () => window.removeEventListener('resize', setReadMore);
  }, []);

  const handleOpenInstructions = React.useCallback(() => {
    setInstructionIsModalOpen(true);
  }, []);

  const handleOpenEditModal = React.useCallback(() => {
    setIsEditModalOpen(true);
  }, []);

  const handleSaveTitle = React.useCallback((name: string) => {
    onChangeName(name);
    setIsEditModalOpen(false);
  }, [onChangeName]);

  return (
    <>
      <div className="flex justify-between mt-4">
        <div className="w-full break-words">
          {displayedTitle && (
            <h3
              className="text-base text-low font-bold"
              data-testid="card-title"
              aria-label={
                mandatory
                  ? `${displayedTitle} - ${intl.formatMessage({ id: 'validation.required', defaultMessage: 'Please answer to continue.' })}`
                  : `${displayedTitle}`
              }
            >
              {mandatory && <MandatorySymbol />}
              {displayedTitle}
            </h3>
          )}
          {!displayedTitle && placeholder && <span className="italic">{placeholder}</span>}
        </div>
        {editable && (
          <span className="cursor-pointer ml-1" onClick={handleOpenEditModal} data-testid="edit-title">
            <Icon className="fb-icon--edit text-2xl text-default" />
          </span>
        )}
      </div>
      <div className="flex flex-col">
        <span ref={descriptionRef} className="text-s line-clamp-2">{displayedDescription}</span>
        {isReadMoreVisible && (
          <span
            className="font-bold underline cursor-pointer text-s"
            onClick={handleOpenInstructions}
            data-testid="read-more"
          >
            <FormattedMessage id="readMore" defaultMessage="Read more" />
          </span>
        )}
      </div>
      <Modal
        open={isInstructionModalOpen}
        onClose={setInstructionIsModalOpen}
        className="w-full max-w-lg kiosk:max-w-4xl kioskSharebox:max-w-2xl"
        title={<h2>{displayedTitle}</h2>}
      >
        {displayedDescription}
      </Modal>
      {isEditModalOpen && <Prompt value={displayedTitle} placeholder={placeholder} title={<FormattedMessage id="vehicleCheck.editName" defaultMessage="Edit name" tagName="h2" />} onClose={setIsEditModalOpen} onSave={handleSaveTitle} />}
    </>
  );
};

export default Content;
